/**
 * Constellation — SVG pipeline canvas (900×520 viewBox).
 *
 * Renders three lane bands (Supply / Match / Demand), seven Wells,
 * SVG connector arrows between wells, and entity Dots fetched from the API.
 *
 * Polling cadences (BRD §7.2):
 *   pipeline-counts  → 30 s
 *   pipeline-dots    → 60 s per well
 *   agent runs       → 15 s (for acting state overlay)
 *
 * Props:
 *   onSelectDot(dot) — fired when a dot is clicked
 */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { getAdminToken } from "@/lib/authStorage";
import Well from "./Well";

// ── Auth helper ───────────────────────────────────────────────────────────────

function authHeaders() {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Lane / well geometry ──────────────────────────────────────────────────────

const VIEWBOX_W = 900;
const VIEWBOX_H = 520;

const LANES = [
  {
    id: "supply",
    label: "SUPPLY",
    y: 20,
    height: 155,
    background: "rgba(23,128,61,0.04)",
    borderColor: "rgba(23,128,61,0.18)",
  },
  {
    id: "match",
    label: "MATCH",
    y: 193,
    height: 155,
    background: "rgba(220,223,217,0.03)",
    borderColor: "rgba(220,223,217,0.10)",
  },
  {
    id: "demand",
    label: "DEMAND",
    y: 366,
    height: 155,
    background: "rgba(78,87,79,0.03)",
    borderColor: "rgba(78,87,79,0.10)",
  },
];

// Wells: x, y, width, height in the 900×520 viewBox
const WELLS = [
  // Supply row y=40, height=130
  { id: "sourced",   label: "Sourced",   lane: "supply", x: 20,  y: 40,  width: 200, height: 130 },
  { id: "vetting",   label: "Vetting",   lane: "supply", x: 350, y: 40,  width: 200, height: 130 },
  { id: "active",    label: "Active",    lane: "supply", x: 680, y: 40,  width: 200, height: 130 },
  // Match row y=210, height=130
  { id: "engaged",   label: "Engaged",   lane: "match",  x: 200, y: 210, width: 500, height: 130 },
  // Demand row y=380, height=130
  { id: "returning", label: "Returning", lane: "demand", x: 20,  y: 380, width: 200, height: 130 },
  { id: "brief",     label: "Brief",     lane: "demand", x: 350, y: 380, width: 200, height: 130 },
  { id: "visitor",   label: "Visitor",   lane: "demand", x: 680, y: 380, width: 200, height: 130 },
];

// ── Arrow connector definitions ───────────────────────────────────────────────
// from/to reference well ids; points are computed from well geometry helpers

function wellCenter(wellId) {
  const w = WELLS.find((w) => w.id === wellId);
  if (!w) return { x: 0, y: 0 };
  return { x: w.x + w.width / 2, y: w.y + w.height / 2 };
}

function wellRight(wellId) {
  const w = WELLS.find((w) => w.id === wellId);
  if (!w) return { x: 0, y: 0 };
  return { x: w.x + w.width, y: w.y + w.height / 2 };
}

function wellLeft(wellId) {
  const w = WELLS.find((w) => w.id === wellId);
  if (!w) return { x: 0, y: 0 };
  return { x: w.x, y: w.y + w.height / 2 };
}

function wellBottom(wellId) {
  const w = WELLS.find((w) => w.id === wellId);
  if (!w) return { x: 0, y: 0 };
  return { x: w.x + w.width / 2, y: w.y + w.height };
}

function wellTop(wellId) {
  const w = WELLS.find((w) => w.id === wellId);
  if (!w) return { x: 0, y: 0 };
  return { x: w.x + w.width / 2, y: w.y };
}

// Arrow: straight line from A to B with an arrowhead marker
function ConnectorArrow({ fromPt, toPt, dasharray, opacity = 0.55, id }) {
  const dx = toPt.x - fromPt.x;
  const dy = toPt.y - fromPt.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  // shorten by 8px on each end so we don't overlap well borders
  const ux = dx / len;
  const uy = dy / len;
  const x1 = fromPt.x + ux * 8;
  const y1 = fromPt.y + uy * 8;
  const x2 = toPt.x - ux * 8;
  const y2 = toPt.y - uy * 8;

  return (
    <line
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      stroke="var(--color-green)"
      strokeWidth={1}
      strokeDasharray={dasharray || "4 3"}
      strokeLinecap="round"
      opacity={opacity}
      markerEnd={`url(#mc-arrow-${id})`}
    />
  );
}

function ArrowMarkers() {
  return (
    <defs>
      {/* Standard forward marker */}
      <marker
        id="mc-arrow-fwd"
        viewBox="0 0 8 8"
        refX="6"
        refY="4"
        markerWidth="6"
        markerHeight="6"
        orient="auto"
      >
        <path d="M0,0 L8,4 L0,8 Z" fill="rgba(23,128,61,0.7)" />
      </marker>
      {/* Return path marker (more muted) */}
      <marker
        id="mc-arrow-return"
        viewBox="0 0 8 8"
        refX="6"
        refY="4"
        markerWidth="6"
        markerHeight="6"
        orient="auto"
      >
        <path d="M0,0 L8,4 L0,8 Z" fill="rgba(23,128,61,0.4)" />
      </marker>
    </defs>
  );
}

// ── Connector layout ──────────────────────────────────────────────────────────

function Connectors() {
  // Supply horizontal: sourced→vetting→active
  const s_to_v_from = wellRight("sourced");
  const s_to_v_to = wellLeft("vetting");
  const v_to_a_from = wellRight("vetting");
  const v_to_a_to = wellLeft("active");

  // active→engaged (diagonal down-left)
  const a_to_e_from = wellBottom("active");
  const a_to_e_to = wellTop("engaged");
  // Adjust to right edge of engaged
  const a_to_e_from_adj = { x: a_to_e_from.x, y: a_to_e_from.y };
  const a_to_e_to_adj = {
    x: WELLS.find((w) => w.id === "engaged").x + WELLS.find((w) => w.id === "engaged").width - 40,
    y: WELLS.find((w) => w.id === "engaged").y,
  };

  // Demand horizontal: visitor→brief→engaged (reversed — seekers flow left toward match)
  const vis_to_br_from = wellLeft("visitor");
  const vis_to_br_to = wellRight("brief");
  const br_to_e_from = wellLeft("brief");
  const br_to_e_to = {
    x: WELLS.find((w) => w.id === "engaged").x + 40,
    y: WELLS.find((w) => w.id === "engaged").y + WELLS.find((w) => w.id === "engaged").height,
  };

  // Return path: engaged→returning (dashed differently)
  const e_to_ret_from = {
    x: WELLS.find((w) => w.id === "engaged").x,
    y: WELLS.find((w) => w.id === "engaged").y + WELLS.find((w) => w.id === "engaged").height / 2,
  };
  const e_to_ret_to = wellRight("returning");

  return (
    <g className="mc-connectors" aria-hidden="true">
      {/* Supply flow */}
      <line
        x1={s_to_v_from.x + 8}
        y1={s_to_v_from.y}
        x2={s_to_v_to.x - 8}
        y2={s_to_v_to.y}
        stroke="var(--color-green)"
        strokeWidth={1}
        strokeDasharray="4 3"
        strokeLinecap="round"
        opacity={0.55}
        markerEnd="url(#mc-arrow-fwd)"
      />
      <line
        x1={v_to_a_from.x + 8}
        y1={v_to_a_from.y}
        x2={v_to_a_to.x - 8}
        y2={v_to_a_to.y}
        stroke="var(--color-green)"
        strokeWidth={1}
        strokeDasharray="4 3"
        strokeLinecap="round"
        opacity={0.55}
        markerEnd="url(#mc-arrow-fwd)"
      />

      {/* active → engaged diagonal */}
      <line
        x1={a_to_e_from_adj.x}
        y1={a_to_e_from_adj.y + 8}
        x2={a_to_e_to_adj.x}
        y2={a_to_e_to_adj.y - 8}
        stroke="var(--color-green)"
        strokeWidth={1}
        strokeDasharray="4 3"
        strokeLinecap="round"
        opacity={0.45}
        markerEnd="url(#mc-arrow-fwd)"
      />

      {/* Demand flow: visitor→brief (reversed label direction - seekers approach from right) */}
      <line
        x1={vis_to_br_from.x - 8}
        y1={vis_to_br_from.y}
        x2={vis_to_br_to.x + 8}
        y2={vis_to_br_to.y}
        stroke="var(--color-green)"
        strokeWidth={1}
        strokeDasharray="4 3"
        strokeLinecap="round"
        opacity={0.45}
        markerEnd="url(#mc-arrow-fwd)"
      />
      {/* brief → engaged */}
      <line
        x1={br_to_e_from.x - 8}
        y1={br_to_e_from.y}
        x2={br_to_e_to.x}
        y2={br_to_e_to.y + 8}
        stroke="var(--color-green)"
        strokeWidth={1}
        strokeDasharray="4 3"
        strokeLinecap="round"
        opacity={0.45}
        markerEnd="url(#mc-arrow-fwd)"
      />

      {/* Return path: engaged→returning (dashed differently, muted) */}
      <line
        x1={e_to_ret_from.x - 8}
        y1={e_to_ret_from.y}
        x2={e_to_ret_to.x + 8}
        y2={e_to_ret_to.y}
        stroke="var(--color-green)"
        strokeWidth={1}
        strokeDasharray="2 5"
        strokeLinecap="round"
        opacity={0.30}
        markerEnd="url(#mc-arrow-return)"
      />
    </g>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <g className="mc-constellation-empty">
      <text
        x={VIEWBOX_W / 2}
        y={VIEWBOX_H / 2 - 10}
        textAnchor="middle"
        fill="var(--color-slate-400)"
        fontSize={18}
        fontFamily="var(--font-display)"
        fontStyle="italic"
      >
        Quiet on the runway
      </text>
      <text
        x={VIEWBOX_W / 2}
        y={VIEWBOX_H / 2 + 16}
        textAnchor="middle"
        fill="var(--color-slate-600)"
        fontSize={11}
        fontFamily="var(--font-sans)"
      >
        No entities in the pipeline right now.
      </text>
    </g>
  );
}

// ── Error banner ──────────────────────────────────────────────────────────────

function ErrorBanner({ message }) {
  return (
    <g className="mc-constellation-error">
      <rect
        x={10}
        y={6}
        width={VIEWBOX_W - 20}
        height={26}
        rx={4}
        fill="rgba(220,50,50,0.12)"
        stroke="rgba(220,50,50,0.3)"
        strokeWidth={1}
      />
      <text
        x={20}
        y={24}
        fill="var(--color-critical)"
        fontSize={11}
        fontFamily="var(--font-sans)"
      >
        {message}
      </text>
    </g>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Constellation({ onSelectDot }) {
  const [dots, setDots] = useState({}); // { [wellId]: dot[] }
  const [counts, setCounts] = useState({});
  const [runningRuns, setRunningRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dotsReady, setDotsReady] = useState(false);

  const wellIds = WELLS.map((w) => w.id);

  // ── Fetch pipeline counts ──────────────────────────────────────────────────

  const fetchCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/ops/pipeline-counts`, {
        headers: authHeaders(),
      });
      setCounts(res.data || {});
      setError(null);
    } catch (err) {
      setError("Failed to load pipeline counts — " + (err.message || "unknown error"));
    }
  }, []);

  // ── Fetch dots for all wells ───────────────────────────────────────────────

  const fetchAllDots = useCallback(async () => {
    const results = {};
    await Promise.allSettled(
      wellIds.map(async (wellId) => {
        try {
          const res = await axios.get(
            `${API}/admin/ops/pipeline-dots?well=${wellId}`,
            { headers: authHeaders() }
          );
          results[wellId] = res.data?.dots || [];
        } catch {
          results[wellId] = [];
        }
      })
    );
    setDots(results);
    setDotsReady(true);
  }, [wellIds]);

  // ── Fetch running agent runs ───────────────────────────────────────────────

  const fetchRuns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/ops/runs?limit=50`, {
        headers: authHeaders(),
      });
      const runs = res.data?.runs || [];
      setRunningRuns(runs.filter((r) => r.status === "running"));
    } catch {
      // non-fatal
    }
  }, []);

  // ── Polling setup ──────────────────────────────────────────────────────────

  useEffect(() => {
    // Initial load
    Promise.all([fetchCounts(), fetchAllDots(), fetchRuns()]).finally(() =>
      setLoading(false)
    );

    const countsTimer = setInterval(fetchCounts, 30_000);
    const dotsTimer = setInterval(fetchAllDots, 60_000);
    const runsTimer = setInterval(fetchRuns, 15_000);

    return () => {
      clearInterval(countsTimer);
      clearInterval(dotsTimer);
      clearInterval(runsTimer);
    };
  }, [fetchCounts, fetchAllDots, fetchRuns]);

  // ── Derived state ──────────────────────────────────────────────────────────

  const totalDots = Object.values(dots).reduce((sum, arr) => sum + arr.length, 0);
  const isEmpty = dotsReady && totalDots === 0;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      className="mc-constellation"
      style={{
        width: "100%",
        maxWidth: "100%",
        position: "relative",
      }}
    >
      <svg
        viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
        preserveAspectRatio="xMidYMid meet"
        className="mc-constellation-svg"
        style={{
          width: "100%",
          height: "520px",
          display: "block",
        }}
        role="img"
        aria-label="Pipeline constellation — entity flow from supply through match to demand"
      >
        <ArrowMarkers />

        {/* Lane bands */}
        {LANES.map((lane) => (
          <g key={lane.id} className={`mc-lane mc-lane--${lane.id}`}>
            <rect
              x={0}
              y={lane.y}
              width={VIEWBOX_W}
              height={lane.height}
              fill={lane.background}
              stroke={lane.borderColor}
              strokeWidth={1}
              strokeDasharray="4 4"
              rx={0}
            />
            {/* Lane label */}
            <text
              x={8}
              y={lane.y + 14}
              fill="var(--color-slate-500)"
              fontSize={9}
              fontFamily="var(--font-sans)"
              letterSpacing="0.18em"
            >
              {lane.label}
            </text>
          </g>
        ))}

        {/* Connector arrows (below wells) */}
        <Connectors />

        {/* Wells */}
        {WELLS.map((well) => (
          <Well
            key={well.id}
            well={well}
            dots={dotsReady ? (dots[well.id] || []) : []}
            runningRuns={runningRuns}
            onSelectDot={onSelectDot}
          />
        ))}

        {/* Loading fade-in overlay — just a subtle opacity change handled via CSS */}
        {loading && (
          <rect
            x={0}
            y={0}
            width={VIEWBOX_W}
            height={VIEWBOX_H}
            fill="rgba(0,0,0,0.0)"
            className="mc-constellation-loading"
          />
        )}

        {/* Empty state */}
        {isEmpty && <EmptyState />}

        {/* Error banner */}
        {error && !loading && <ErrorBanner message={error} />}
      </svg>
    </div>
  );
}
